import { useEffect, useState } from 'react';
import {
  clearTutorSystemCapabilitiesCache,
  fetchTutorSystemCapabilities,
} from '../modules/tutor/api/tutorCapabilities.client';

export function useTutorSystemCapabilities(options?: { force?: boolean }): {
  liveAiQuotaPoolEnabled: boolean | null;
  schoolAiAllocationsEnabled: boolean | null;
  governanceMode: 'flexible' | 'strict' | null;
  quotaRuntimeV2Enabled: boolean | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
} {
  const force = options?.force === true;
  const [liveAiQuotaPoolEnabled, setLiveAiQuotaPoolEnabled] = useState<boolean | null>(null);
  const [schoolAiAllocationsEnabled, setSchoolAiAllocationsEnabled] = useState<boolean | null>(null);
  const [governanceMode, setGovernanceMode] = useState<'flexible' | 'strict' | null>(null);
  const [quotaRuntimeV2Enabled, setQuotaRuntimeV2Enabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        if (force) clearTutorSystemCapabilitiesCache();
        const r = await fetchTutorSystemCapabilities({ force });
        if (!cancelled) {
          setLiveAiQuotaPoolEnabled(r.live_ai_quota_pool_enabled);
          setSchoolAiAllocationsEnabled(!!r.school_ai_allocations_enabled);
          setGovernanceMode(r.governance_mode === 'strict' ? 'strict' : 'flexible');
          setQuotaRuntimeV2Enabled(!!r.quota_runtime_v2_enabled);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setLiveAiQuotaPoolEnabled(null);
          setSchoolAiAllocationsEnabled(null);
          setGovernanceMode(null);
          setQuotaRuntimeV2Enabled(null);
          setError(e instanceof Error ? e : new Error('Failed to load tutor capabilities'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [force, tick]);

  const refresh = async () => {
    clearTutorSystemCapabilitiesCache();
    setTick((t) => t + 1);
  };

  return {
    liveAiQuotaPoolEnabled,
    schoolAiAllocationsEnabled,
    governanceMode,
    quotaRuntimeV2Enabled,
    loading,
    error,
    refresh,
  };
}
